<!--
Purchase order filters logic:
  filterSelect component is a generic filterdropdown component that is reused to display all types of filters.
  Direct reports are already computed using directReports API and displayed
  onFilterSelected passes the event to this.filterList which is a generic filter that filters on
  all current filters applied and returns a filtered object.
  We check for direct reports in both filter events (when roles are clicked, and when direct report names are clicked).
  Filters are saved in vuex using poTableUserPreferences object and reapplied when user revisits the tab.
  We maintain currentPOFiltersList object to ensure unchecked filter items persist separately in vuex.
  This is required because not all values from purchaseOrdersFormattedList can be used.
  They cannot be used because the PO dashboard is launched with all roles applied
  (and POs that do not apply to the roles criteria are not shown to the user).
-->

<template>
<div v-if="!isLoading">
  <div class="row">
    <filter-select
        ref="poSupplierNameFilter"
        :filter-title="'Supplier name'"
        :filter-options="currentPOFiltersList.supplierNameList"
        :isRolesChanged="isRolesFilterChanged"
        :saved-user-filter="supplierNameSavedFilter"
        @optionsSelected="onFilterSelected($event, 'supplierName')"
        @allOptionsCleared="onAllOptionsCleared($event,'supplierNameAllUnchecked')"
        :allOptionsUnchecked="poTableUserPreferences.filter.supplierNameAllUnchecked"
        :key="filterKeys.supplierNameKey"
    />
    <filter-select
        ref="costCenterFilter"
        :filter-title="'Cost center'"
        :filter-options="currentPOFiltersList.costCenterList"
        :saved-user-filter="costCenterFilter"
        :isRolesChanged="isRolesFilterChanged"
        @optionsSelected="onFilterSelected($event, 'costCenter')"
        @allOptionsCleared="onAllOptionsCleared($event,'costCenterAllUnchecked')"
        :allOptionsUnchecked="poTableUserPreferences.filter.costCenterAllUnchecked"
        :key="filterKeys.costCenterKey"
    />
    <filter-select
        ref="poStatusFilter"
        :filter-title="'Purchase order status'"
        :filter-options="currentPOFiltersList.statusList"
        :saved-user-filter="originalStatusFilter"
        :isRolesChanged="isRolesFilterChanged"
        @optionsSelected="onFilterSelected($event, 'originalStatus')"
        @allOptionsCleared="onAllOptionsCleared($event,'originalStatusAllUnchecked')"
        :allOptionsUnchecked="poTableUserPreferences.filter.originalStatusAllUnchecked"
        :key="filterKeys.poStatusKey"
    />
  </div>
  <div class="row" >
    <filter-select
        ref="rolesFilter"
        :filter-title="'Roles'"
        :filter-options="rolesList"
        :saved-user-filter="rolesSavedFilter"
        @optionsSelected="onFilterSelected($event, 'roles')"
        @allOptionsCleared="onAllOptionsCleared($event,'rolesAllUnchecked')"
        :isRolesChanged="isRolesFilterChanged"
        :allOptionsUnchecked="poTableUserPreferences.filter.rolesAllUnchecked"
        :filterSubDesc="directReportsFilterSelected ? directReportsSubDesc : ''"
        :key="filterKeys.rolesKey"
    />
    <filter-select
        v-if="currentFilterIncludesDirectReports"
        ref="namesFilter"
        :filter-title="this.DIRECT_REPORT_STRING"
        :filter-options="currentPOFiltersList.usersList"
        :saved-user-filter="namesSavedFilter"
        :isRolesChanged="isRolesFilterChanged"
        @optionsSelected="onFilterSelected($event, 'names')"
        @allOptionsCleared="onAllOptionsCleared($event,'namesAllUnchecked')"
        :allOptionsUnchecked="poTableUserPreferences.filter.namesAllUnchecked"
        :key="filterKeys.userKey"
    />
    <span class="col-lg-3 px-4 py-1  d-flex align-items-center" v-if="showClearFilters"><span @click="clearFilters" class="solution-link">Clear filters</span></span>
  </div>
</div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex"
import InvoicesPOFiltersMixin from "@/mixins/InvoicesPOFiltersMixin";
import FilterSelect from "@/components/InvoiceAndPurchaseOrders/FilterSelect";
import InvoicesAndPOMixin from "@/mixins/InvoicesAndPOMixin";

export default {
  name: "PurchaseOrderFilters",
  components: {FilterSelect},
  props: ["isRolesFilterChanged"],
  computed: {
    ...mapGetters(["isLoading","isNamesFilterLoading","purchaseOrdersFormattedList","currentUser","errorMessage","isLoading", "poTableUserPreferences","viewingAPUserDataFor","isAPAdmin", "userData","rolesToNamesMap","apUserId","isNamesFilterLoading","rolesObj","poFiltersList"]),
    supplierNameSavedFilter() {
      return this.poTableUserPreferences.filter.supplierName
    },
    originalStatusFilter() {
      return this.poTableUserPreferences.filter.originalStatus
    },
    namesSavedFilter() {
      return this.poTableUserPreferences.filter.names
    },
    rolesSavedFilter() {
      return this.poTableUserPreferences.filter.roles
    },
    costCenterFilter() {
      return this.poTableUserPreferences.filter.costCenter
    },
    rolesList() {
      return this.isLoading ? [] : this.sortIgnoreCase(Object.values(this.rolesObj))
    },
    currentFilterIncludesDirectReports() {
      return this.rolesSavedFilter && this.rolesSavedFilter.length > 0 && this.rolesSavedFilter.indexOf(this.DIRECT_REPORT_STRING) !== -1
    },
    showClearFilters() {
      return this.poTableUserPreferences.showClearFilters || this.currentFiltersAreActive || this.rolesFilterIsActive
    },
    currentFiltersAreActive() {
      return this.supplierNameSavedFilter.length > 0 || this.originalStatusFilter.length > 0 || this.namesFilterIsActive || this.costCenterFilter.length > 0 ||
          this.poTableUserPreferences.filter.supplierNameAllUnchecked || this.poTableUserPreferences.filter.namesAllUnchecked ||
          this.poTableUserPreferences.rolesAllUnchecked || this.poTableUserPreferences.filter.originalStatusAllUnchecked || this.poTableUserPreferences.filter.costCenterAllUnchecked
    },
    rolesFilterIsActive() {
      if(this.rolesSavedFilter && this.rolesSavedFilter.length > 0)
        return !this.isArrayEqual(this.rolesSavedFilter, Object.values(this.rolesObj))
      else
        return this.poTableUserPreferences.filter.rolesAllUnchecked
    },
    namesFilterIsActive() {
      if(this.namesSavedFilter && this.namesSavedFilter.length > 0 && this.rolesToNamesMap.directReports && this.rolesToNamesMap.directReports.length > 0 )
        return !this.isArrayEqual(this.namesSavedFilter, Object.values(this.rolesToNamesMap.directReports))
      else
        return this.poTableUserPreferences.filter.namesAllUnchecked
    }
  },
  mixins: [InvoicesPOFiltersMixin,InvoicesAndPOMixin],
  watch: {
    //Update filters once new direct reports (if any) are fetched, and clear filters selection
    isNamesFilterLoading(loading) {
      if(!loading) {
        this.createInitialDropdownList()
        this.clearFilters()
      }
    }
  },
  data() {
    return {
      currentPOFiltersList: {
        costCenterList: [],
        supplierNameList: [],
        usersList: [],
        statusList: [],
        isFiltersReloaded: false
      },
      filterKeys: {
        userKey: 10,
        rolesKey: 20,
        supplierNameKey: 30,
        costCenterKey: 40,
        poStatusKey: 50
      },
      directReportsSubDesc: 'Use the \'My direct reports\' filter to further scope the records.',
      activeRoleFilters: [],
      directReportsFilterSelected: false,
      filtersSelected:false,
      filteredPOList: null
    }
  },
  beforeDestroy() {
    this.currentPOFiltersList.isFiltersReloaded = true
    this.$emit('onPurchaseOrderRolesFilterChanged', false)
    this.setPOFiltersList(this.currentPOFiltersList)
  },
  mounted() {
    this.createInitialDropdownList()
  },
  methods: {
    ...mapMutations(["setPOFiltersList"]),
    clearFilters() {
      this.resetPoTableUserPreferences()
      this.filterList()
      this.filterKeys.rolesKey++
      this.filterKeys.supplierNameKey++
      this.filterKeys.costCenterKey++
      this.filterKeys.poStatusKey++
      this.updateNamesBasedOnCurrentFilters()
      this.poFiltersList.isFiltersReloaded = false
      this.$emit('onPurchaseOrderRolesFilterChanged', false)
      this.$emit('onSetVgtSortedArraySum', false)
      this.$emit('onUpdateTotalValueWithSortedData')
    },
    isArrayEqual(array1, array2) {
      let arraySource = JSON.parse(JSON.stringify(array1))
      let arrayDestination = JSON.parse(JSON.stringify(array2))
      return arraySource.sort().join(',') === arrayDestination.sort().join(',')
    },
    //Below method externally applies roles filter logic such that costCenter, SupplierName and PO Status dropdown values are rendered and never changed.
    //It is upto the user to manually check and uncheck but the list will remain static.
    createInitialDropdownList() {
      let supplierNameFilter = []
      let costCenterFilter = []
      let poStatusFilter = []
      if(this.purchaseOrdersFormattedList && this.purchaseOrdersFormattedList.length > 0) {
        this.purchaseOrdersFormattedList.forEach(po => {
          if(this.isEligiblePOForFilters(po)) {
            !supplierNameFilter.includes(po.supplierName) && this.isValidString(po.supplierName) ? supplierNameFilter.push(po.supplierName) : ''
            !poStatusFilter.includes(po.originalStatus) && this.isValidString(po.originalStatus) ? poStatusFilter.push(po.originalStatus) : ''
            if(po.poAdditionalData && po.poAdditionalData.length > 0) {
              po.poAdditionalData.forEach((data) => {
                !costCenterFilter.includes(data.costCenter) && this.isValidString(data.costCenter)? costCenterFilter.push(data.costCenter) : ''
              })
            }
          }
        })
      }
        this.currentPOFiltersList.supplierNameList = this.sortIgnoreCase(supplierNameFilter)
        this.currentPOFiltersList.costCenterList = this.sortIgnoreCase(costCenterFilter)
        this.currentPOFiltersList.statusList = this.sortIgnoreCase(poStatusFilter)
        if(this.poTableUserPreferences.filter.originalStatus.length === 0)
          this.$refs && this.$refs.poStatusFilter ? this.$refs.poStatusFilter.selectedOptions = JSON.parse(JSON.stringify(poStatusFilter)) : ''
        if(this.poTableUserPreferences.filter.supplierName.length === 0)
          this.$refs && this.$refs.poSupplierNameFilter ? this.$refs.poSupplierNameFilter.selectedOptions = JSON.parse(JSON.stringify(supplierNameFilter)) : ''
        if(this.poTableUserPreferences.filter.costCenter.length === 0)
          this.$refs && this.$refs.costCenterFilter ? this.$refs.costCenterFilter.selectedOptions = JSON.parse(JSON.stringify(costCenterFilter)) : ''
    },
    isEligiblePOForFilters(po) {
      let userId = this.viewingAPUserDataFor ? this.apUserId : this.currentUser.userName
      let isEligible = false
      if(po.requestedBy === userId
      || po.createdBy === userId
      || (po.poAdditionalData && po.poAdditionalData.some(additionalData => additionalData.costCenterOwner === userId))
      || (po.poAdditionalData && po.poAdditionalData.some(additionalData => additionalData.costCenterFbp === userId))) {
        isEligible = true
      } else if(this.rolesToNamesMap.directReports && this.rolesToNamesMap.directReports.length > 0) {
        if(this.rolesToNamesMap.directReports.includes(po.requestedByFullName)) {
          isEligible = true
        }
      }
      return isEligible
    },
    onFilterSelected(filteredItems, filteredVariable) {
      let savedFilterNames = this.poTableUserPreferences.filter.names
      let isDirectReportsRequested = false

      if(filteredVariable === 'roles') {
        // We need to save filter preference here as we update
        // filteredItems and filteredVariable to 'names' and 'names list' to handle direct reports use case
        this.saveFilterPreferences(filteredItems, filteredVariable)
        let filterObj = this.updateBasedOnFilterCriteria(filteredItems, filteredVariable)
        filteredItems = filterObj.filteredItems
        filteredVariable = filterObj.filteredVariable
        isDirectReportsRequested = filterObj.isDirectReportsRequested
      }
      this.$emit('onSetVgtSortedArraySum', false)

      this.saveFilterPreferences(filteredItems, filteredVariable)

      if(this.poFiltersList.isFiltersReloaded && isDirectReportsRequested) {
        this.poTableUserPreferences.filter.names = savedFilterNames
      }

      this.setPOTableUserPreferences(this.poTableUserPreferences)

      this.filterList()

    },
    saveFilterPreferences(filteredItems, filteredVariable) {
      if(filteredItems && filteredItems.length>0){
        this.filtersSelected =true;
        this.poTableUserPreferences.filter[filteredVariable] = filteredItems
      }else{
        this.filteredPOList = [];
        this.poTableUserPreferences.filter[filteredVariable] = []
        this.filtersSelected = false;
        this.$emit('onFilteredPOListUpdated', this.filteredPOList)
      }
    },
    updateBasedOnFilterCriteria(filteredItems, filteredVariable) {
      this.$emit('onPurchaseOrderRolesFilterChanged', true)
      let filterObj = {}
      filterObj.filteredItems =  filteredItems
      filterObj.filteredVariable = filteredVariable
      if(filteredItems && filteredItems.length > 0
          && filteredItems.indexOf(this.DIRECT_REPORT_STRING) !== -1
          && this.rolesToNamesMap.directReports && this.rolesToNamesMap.directReports.length > 0)
        this.directReportsFilterSelected = true
      else {
        this.directReportsFilterSelected = false
        this.poTableUserPreferences.filter.names = []
      }

      this.activeRoleFilters = filteredItems
      let usersArray = []
      usersArray = this.getCurrentUserSelection()
      if(this.poTableUserPreferences.filter.roles && this.poTableUserPreferences.filter.roles.length > 0 && filteredItems && filteredItems.length > 0) {
        //If roles are saved, update usersArray based on saved filter.
        usersArray = this.updateBasedOnSavedFilters(usersArray)
      }
      if(filteredItems && filteredItems.length > 0 && filteredItems.indexOf(this.DIRECT_REPORT_STRING) !== -1) {
        filterObj.isDirectReportsRequested = true
      }
      //We're not filtering table by just roles.
      //Below we assign filteredItems and variable to Names specific criteria
      //That way table can be filtered by names.
      filterObj.filteredItems =  JSON.parse(JSON.stringify(usersArray))
      filterObj.filteredVariable = 'names'
      return filterObj
    },
    getCurrentUserSelection() {
      if(this.poTableUserPreferences.filter.namesAllUnchecked) {
        this.poTableUserPreferences.filter.names = []
        return []
      } return this.rolesToNamesMap.directReports && this.rolesToNamesMap.directReports.length > 0 ? this.rolesToNamesMap.directReports : []
    },
    updateBasedOnSavedFilters(usersArray) {
      if (this.poTableUserPreferences.filter.roles && this.poTableUserPreferences.filter.roles.length > 0){
        this.poTableUserPreferences.filter.roles.forEach((role) => {
          if(this.poTableUserPreferences.filter.names && this.poTableUserPreferences.filter.names.length > 0) {
            usersArray = JSON.parse(JSON.stringify(this.poTableUserPreferences.filter.names))
          } else {
            if(role === this.DIRECT_REPORT_STRING && !this.poTableUserPreferences.filter.namesAllUnchecked) {
              if (this.rolesToNamesMap.directReports && this.rolesToNamesMap.directReports.length > 0){
                this.rolesToNamesMap.directReports.forEach((name) => {
                  if(usersArray.indexOf(name) === -1) {
                    usersArray.push(name)
                  }
                })
              }
            }
          }
        })
        this.updateNamesBasedOnCurrentFilters()
      }
      return usersArray
    },
    validateForNonDirectReport(item, userId) {
      //if more than directReports is selected we run below two functions that validates other role filters if Direct Reports is also one of them.
      if(this.activeRoleFilters && this.activeRoleFilters.some(item => item !== this.DIRECT_REPORT_STRING)) {
        return this.validateForNonDirectReportRoles(userId , item.requestedBy) || this.validateForNonDirectReportRoles(userId,item.requestedBy ,item.createdBy )
      }
    },

    validatePOForCostCenterAndFBPOwners(po, userId) {
      if(this.$refs && this.$refs.rolesFilter && this.$refs.rolesFilter.selectedOptions) {
        let selectedRoles = this.$refs.rolesFilter.selectedOptions
        if(selectedRoles.indexOf(this.rolesObj.costCenterOwner) !== -1) {
          if(po && po.poAdditionalData && po.poAdditionalData.length > 0) {
            return po.poAdditionalData.some(po => {
              return po.costCenterOwner === userId
            })
          }
        } else if(selectedRoles.indexOf(this.rolesObj.costCenterFbp) !== -1) {
          if(po && po.poAdditionalData && po.poAdditionalData.length > 0) {
            return po.poAdditionalData.some(po => {
              return po.costCenterFbp === userId
            })
          }
        } return false
      }
    },

    updateNamesBasedOnCurrentFilters() {
      let names = []

      if(this.rolesToNamesMap.directReports && this.rolesToNamesMap.directReports.length > 0) {
        names = this.sortIgnoreCase([...new Set(this.rolesToNamesMap.directReports)])
      }

      if(this.poFiltersList.isFiltersReloaded) {
        this.currentPOFiltersList.usersList = (JSON.parse(JSON.stringify(this.poFiltersList.usersList)))
        this.filterKeys.userKey++
      } else {
        this.currentPOFiltersList.usersList = names && names.length > 0 ? this.sortIgnoreCase(names) : names;
        this.filterKeys.userKey++
      }
    },

    validateSelectedRoles(role, searchValue, currentRole, userId) {
        return role === currentRole && searchValue === userId
    },
    validateActiveRoleFilters() {
      //if active roles filter is not set, default it to all roles in the rolesObj object.
      if(this.activeRoleFilters && this.activeRoleFilters.length === 0) {
        this.activeRoleFilters = Object.values(this.rolesObj)
      }
    },
    validateNamesForSelectedRole(currentRole, poObjectFullName, filterObjectFullName) {
      if(this.activeRoleFilters.indexOf(this.DIRECT_REPORT_STRING) !== -1 && this.activeRoleFilters.length === 1) {
        //This condition means only when direct report is checked inside roles and it is the only role selected.
        return poObjectFullName === filterObjectFullName && this.rolesToNamesMap.directReports.indexOf(poObjectFullName) !== -1
      }
      if(this.activeRoleFilters.indexOf(currentRole) !== -1  || this.activeRoleFilters.indexOf(this.DIRECT_REPORT_STRING) !== -1) {
        return poObjectFullName === filterObjectFullName
      } return false
    },
    updatePOFilterCriteria() {
      let filterObj = {}
      //Populate filterObj with keys specific to filter criteria and ignore empty arrays or null keys
      for (let [key, value] of Object.entries(this.poTableUserPreferences.filter)) {
        if(value && value.length > 0) {
          if(value.indexOf(this.DIRECT_REPORT_STRING) === -1)
            filterObj[key] = JSON.parse(JSON.stringify(value))
        }
      }
      return filterObj
    },
    validateForNonDirectReportRoles(userId,requestedBy,createdBy){
      //this validates MYPO's: - requestedBy the currentUser when direct reports filter is checked on.
      if (!createdBy && this.activeRoleFilters && this.activeRoleFilters.length > 0 && this.isRoleSelected(this.REQUESTED_BY_STRING)){
        return (requestedBy === userId)
        //this validates PO's Created By User but for someone else when direct reports filter is checked on.
      }else if ( createdBy && this.activeRoleFilters && this.activeRoleFilters.length > 0 && this.isRoleSelected(this.CREATED_BY_STRING)){
        return  createdBy === userId && createdBy !== requestedBy
      }
      return false
    },
    filterList() {
      this.filtersSelected = true;
      if(this.poTableUserPreferences.filter.supplierNameAllUnchecked ||
          this.poTableUserPreferences.filter.originalStatusAllUnchecked ||
          this.poTableUserPreferences.filter.costCenterAllUnchecked ||
          this.poTableUserPreferences.filter.rolesAllUnchecked || (!this.purchaseOrdersFormattedList || this.purchaseOrdersFormattedList.length === 0)) {
        this.filteredPOList = []
        this.$emit('onFilteredPOListUpdated', this.filteredPOList)
      } else {
        //Filter by union of selected filters which are in memory.
        let selectedFilters = this.updatePOFilterCriteria()

        this.$hhMixpanel.trackEvent("Supplier central - Purchase order filters selected", {filters : Object.keys(selectedFilters)});

        let userId = this.viewingAPUserDataFor ? this.apUserId : this.currentUser.userName

        if(selectedFilters && selectedFilters.roles && selectedFilters.roles.indexOf(this.DIRECT_REPORT_STRING) === -1)
          delete selectedFilters.names

        if(this.poTableUserPreferences.filter.namesAllUnchecked)
          selectedFilters.roles = this.rolesSavedFilter

        let that = this

        this.filteredPOList = this.purchaseOrdersFormattedList.filter(function (po) {
          return Object.keys(selectedFilters).every(function (filter) {
            if(filter === 'costCenter') {
              if(po.poAdditionalData && po.poAdditionalData.length > 0) {
                return po.poAdditionalData.some(function (data) {
                  return selectedFilters[filter].includes(data.costCenter)
                })
              }
            } else if(filter === 'roles') {
              return selectedFilters[filter].some(function(value) {
                if(value !== that.DIRECT_REPORT_STRING) {
                  return that.validateSelectedRoles(that.CREATED_BY_STRING ,po.createdBy,value, userId)
                      || that.validateSelectedRoles(that.REQUESTED_BY_STRING, po.requestedBy, value, userId)
                      || po.poAdditionalData.some(function (data) {
                        return that.validateSelectedRoles(that.COST_CENTER_FBP_STRING, data.costCenterFbp, value, userId)
                            || that.validateSelectedRoles(that.COST_CENTER_OWNER_STRING, data.costCenterOwner, value, userId)
                      })
                }
              })
            } else if(filter === 'names') {
              return selectedFilters[filter].some(function (value) {
                that.validateActiveRoleFilters()
                return that.validateNamesForSelectedRole(that.REQUESTED_BY_STRING,po.requestedByFullName, value)
                    || that.validateForNonDirectReport(po, userId)
                    || that.validatePOForCostCenterAndFBPOwners(po, userId)
              });
            } else if(filter !== 'roles') {
              return selectedFilters[filter].some(function (value) {
                return po[filter] === value;
              });
            } else {
              return po
            }
          });
        });
        this.$emit('onFilteredPOListUpdated', this.filteredPOList)
        this.$emit('onUpdateTotalValueWithSortedData')
      }
    },
    isRoleSelected(role){
      return this.activeRoleFilters && this.activeRoleFilters.some(item => item === role)
    },
    onAllOptionsCleared(event, params) {
      this.poTableUserPreferences.filter[params] = event.status
      this.filterList()
    },
  }
}
</script>

<style scoped>

</style>